// router
import { useTranslation } from 'react-i18next';

// mui
import { Chip, ChipProps } from '@mui/material';

// types
import type { VoucherStatus } from '../../../data/types';

// constants
import {
	PAYMENT_BUCKETS,
	bucketForStatus,
} from '../../pages/ProjectV2/Payments/Payments.constants';

export interface VoucherStatusChipProps extends Omit<ChipProps, 'color' | 'label'> {
	status: VoucherStatus | null | undefined;
}

// Generic labels for voucher statuses that are not part of the three payment
// buckets shown on the Payments page. These vouchers should rarely surface in
// the UI; the labels exist only so a chip in some other surface (e.g. contract
// detail) renders something readable instead of the raw enum.
const FALLBACK_LABEL: Record<string, { label: string; color: ChipProps['color'] }> = {
	GENERATED: { label: 'Generated', color: 'default' },
	CANCELLED: { label: 'Cancelled', color: 'error' },
	DEACTIVATED: { label: 'Deactivated', color: 'error' },
};

export const VoucherStatusChip = ({ status, ...props }: VoucherStatusChipProps) => {
	const { t } = useTranslation();

	if (!status) {
		return <Chip color="default" label={t('na')} size="small" variant="twoTone" {...props} />;
	}

	const bucket = bucketForStatus(status);
	if (bucket) {
		const def = PAYMENT_BUCKETS[bucket];
		return (
			<Chip
				color={def.color}
				label={t(def.i18nKey, def.fallback)}
				size="small"
				variant="twoTone"
				{...props}
			/>
		);
	}

	const fallback = FALLBACK_LABEL[status] ?? { label: status, color: 'default' as const };
	return (
		<Chip
			color={fallback.color}
			label={fallback.label}
			size="small"
			variant="twoTone"
			{...props}
		/>
	);
};
